import type { QuestionDefinition } from '@cyberscore/contracts';
import { Check, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { FileAnswer } from '../documents/DocumentParts';

type Column = { key: string; label: string; type: 'text' | 'number' | 'date' | 'boolean' | 'choice'; options?: string[]; required: boolean }; // prettier-ignore

interface Props {
  q: QuestionDefinition;
  value: unknown;
  /** The value changed; undefined means "nothing usable entered yet". */
  onChange: (v: unknown) => void;
  /** A single tap that is a complete answer (a choice, a rating): save it and move on. */
  onPick: (v: unknown) => void;
  errors: Record<string, string>;
  disabled?: boolean;
  id: string;
  /** For file questions: where uploads go. Without it, file questions can't be answered here. */
  upload?: { assessmentId: string; question: string };
}

/** One-tap inputs (choices, ratings) leave an error about the whole value to the caller to show. */
export function showsValueError(q: QuestionDefinition): boolean {
  if (q.type === 'single_choice' || q.type === 'boolean' || q.type === 'rating') return false;
  return !(q.type === 'entity_selector' && q.options.length > 0 && !q.settings.multiple);
}

/**
 * The input for one question, by type. Values are exactly what the API takes (checked again in the browser
 * with the same rules before sending, and by the core when saving).
 */
export function AnswerInput({ q, value, onChange, onPick, errors, disabled, id, upload }: Props) {
  const err = (field = '') => errors[field] && <div className="error">{errors[field]}</div>;

  switch (q.type) {
    case 'single_choice':
    case 'boolean':
      return <Choices q={q} value={value} onPick={onPick} disabled={disabled} />;
    case 'multiple_choice':
    case 'asset_selector':
      return (
        <>
          <MultiChoices q={q} value={value} onChange={onChange} disabled={disabled} />
          {err()}
        </>
      );
    case 'entity_selector':
      if (q.options.length)
        return q.settings.multiple ? (
          <MultiChoices q={q} value={value} onChange={onChange} disabled={disabled} />
        ) : (
          <Choices q={q} value={value} onPick={onPick} disabled={disabled} />
        );
      return (
        <div className="field">
          <input
            id={id}
            className="input"
            disabled={disabled}
            defaultValue={Array.isArray(value) ? value.join(', ') : ((value as string) ?? '')}
            placeholder={q.settings.multiple ? 'Separate names with commas' : ''}
            onChange={(e) => {
              const t = e.target.value.trim();
              if (!t) return onChange(undefined);
              onChange(
                q.settings.multiple
                  ? t
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean)
                  : t,
              );
            }}
          />
          {err()}
        </div>
      );
    case 'rating':
      return (
        <div className="rating" role="radiogroup" aria-labelledby={`${id}-label`}>
          {q.settings.low_label && <span className="muted">{q.settings.low_label}</span>}
          {Array.from({ length: q.settings.scale }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={value === n}
              className={`rate${value === n ? ' on' : ''}`}
              disabled={disabled}
              onClick={() => onPick(n)}
            >
              {n}
            </button>
          ))}
          {q.settings.high_label && <span className="muted">{q.settings.high_label}</span>}
        </div>
      );
    case 'numeric':
    case 'employee_count':
    case 'percentage': {
      const suffix =
        q.type === 'percentage' ? '%' : q.type === 'employee_count' ? 'people' : (q.settings.unit ?? '');
      const integer = q.type === 'employee_count' || (q.type === 'numeric' && q.settings.integer);
      return (
        <div className="field">
          <div className="affix">
            <NumberField id={id} value={value} integer={integer} disabled={disabled} onChange={onChange} />
            {suffix && <span className="muted">{suffix}</span>}
          </div>
          {err()}
        </div>
      );
    }
    case 'currency': {
      const v = value as { amount?: number } | undefined;
      const cur = q.settings.currency;
      return (
        <div className="field">
          <div className="affix">
            <span className="muted">{cur === 'MYR' ? 'RM' : cur}</span>
            <NumberField
              id={id}
              value={v?.amount}
              disabled={disabled}
              onChange={(n) => onChange(n === undefined ? undefined : { amount: n, currency: cur })}
            />
          </div>
          {err() || err('amount')}
        </div>
      );
    }
    case 'date':
      return (
        <div className="field">
          <input
            id={id}
            type="date"
            className="input narrow"
            disabled={disabled}
            min={q.settings.min}
            max={q.settings.not_in_future ? new Date().toISOString().slice(0, 10) : q.settings.max}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value || undefined)}
          />
          {err()}
        </div>
      );
    case 'date_range': {
      const r = (value as { from?: string; to?: string }) ?? {};
      const set = (k: 'from' | 'to', v: string) => {
        const next = { ...r, [k]: v || undefined };
        onChange(next.from && next.to ? next : undefined);
      };
      return (
        <div className="field">
          <div className="input-row">
            <label className="field grow">
              <span className="hint">From</span>
              <input id={id} type="date" className="input" disabled={disabled} value={r.from ?? ''} onChange={(e) => set('from', e.target.value)} />
            </label>
            <label className="field grow">
              <span className="hint">To</span>
              <input type="date" className="input" disabled={disabled} value={r.to ?? ''} onChange={(e) => set('to', e.target.value)} />
            </label>
          </div>
          {err()}
        </div>
      ); // prettier-ignore
    }
    case 'free_text':
    case 'rich_text': {
      const max = q.settings.max_length;
      const multiline = q.type === 'rich_text' || q.settings.multiline;
      const text = (value as string) ?? '';
      const props = {
        id,
        className: 'input',
        disabled,
        maxLength: max,
        value: text,
        onChange: (e: { target: { value: string } }) =>
          onChange(e.target.value.trim() ? e.target.value : undefined),
      };
      return (
        <div className="field">
          {multiline ? <textarea {...props} rows={5} className="input textarea" /> : <input {...props} />}
          <div className="hint">
            {text.length.toLocaleString()} / {max.toLocaleString()}
          </div>
          {err()}
        </div>
      );
    }
    case 'url':
      return (
        <div className="field">
          <input
            id={id}
            type="url"
            className="input"
            disabled={disabled}
            placeholder={q.settings.https_only ? 'https://' : 'https:// or http://'}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value.trim() || undefined)}
          />
          {err()}
        </div>
      );
    case 'location':
      return (
        <LocationField q={q} value={value} onChange={onChange} errors={errors} disabled={disabled} id={id} />
      );
    case 'matrix':
      return <MatrixField q={q} value={value} onChange={onChange} errors={errors} disabled={disabled} />;
    case 'table':
      return (
        <RowsField
          columns={q.settings.columns}
          label="Row"
          min={q.settings.min_rows}
          max={q.settings.max_rows}
          value={value}
          onChange={onChange}
          errors={errors}
          disabled={disabled}
        />
      );
    case 'repeating_group':
      return (
        <RowsField
          columns={q.settings.fields}
          label={q.settings.item_label}
          min={q.settings.min_items}
          max={q.settings.max_items}
          value={value}
          onChange={onChange}
          errors={errors}
          disabled={disabled}
          stacked
        />
      );
    case 'file_upload':
    case 'image':
      return upload ? (
        <>
          <FileAnswer {...upload} q={q} value={value} onChange={onChange} disabled={disabled} />
          {err()}
        </>
      ) : (
        <div className="notice neutral">Files are uploaded from the assessment’s form or chat.</div>
      );
    case 'api_derived':
    case 'ai_derived':
    case 'system_derived':
      return <div className="notice neutral">This one is filled in for you from your connected systems.</div>;
  }
}

function Choices({
  q,
  value,
  onPick,
  disabled,
}: {
  q: QuestionDefinition;
  value: unknown;
  onPick: (v: unknown) => void;
  disabled?: boolean;
}) {
  // prettier-ignore
  return (
    <div className="opts" role="radiogroup">
      {q.options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={`opt${value === o.value ? ' on' : ''}`}
          disabled={disabled}
          onClick={() => onPick(o.value)}
        >
          <span className="oi" aria-hidden="true">
            {value === o.value && <Check size={14} />}
          </span>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function MultiChoices({
  q,
  value,
  onChange,
  disabled,
}: {
  q: QuestionDefinition;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
}) {
  // prettier-ignore
  const picked = new Set(Array.isArray(value) ? (value as string[]) : []);
  const toggle = (v: string) => {
    const next = new Set(picked);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    // Keep the configured order, so the stored answer does not depend on click order.
    const arr = q.options.map((o) => o.value).filter((x) => next.has(x));
    onChange(arr.length ? arr : undefined);
  };
  return (
    <div className="opts" role="group">
      {q.options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="checkbox"
          aria-checked={picked.has(o.value)}
          className={`opt multi${picked.has(o.value) ? ' on' : ''}`}
          disabled={disabled}
          onClick={() => toggle(o.value)}
        >
          <span className="oi box" aria-hidden="true">
            {picked.has(o.value) && <Check size={14} />}
          </span>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Keeps what was typed, and reports a number only when it is one. */
function NumberField({
  id,
  value,
  integer,
  disabled,
  onChange,
}: {
  id?: string;
  value: unknown;
  integer?: boolean;
  disabled?: boolean;
  onChange: (n: number | undefined) => void;
}) {
  // prettier-ignore
  const [text, setText] = useState(() => (typeof value === 'number' ? String(value) : ''));
  return (
    <input
      id={id}
      className="input narrow"
      inputMode={integer ? 'numeric' : 'decimal'}
      disabled={disabled}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const t = e.target.value.replace(/[,\s]/g, '');
        const n = Number(t);
        onChange(t === '' || !Number.isFinite(n) ? undefined : n);
      }}
    />
  );
}

function LocationField({
  q,
  value,
  onChange,
  errors,
  disabled,
  id,
}: {
  q: Extract<QuestionDefinition, { type: 'location' }>;
  value: unknown;
  onChange: (v: unknown) => void;
  errors: Record<string, string>;
  disabled?: boolean;
  id: string;
}) {
  // prettier-ignore
  const v = (value as Record<string, string>) ?? { country: 'MY' };
  const g = q.settings.granularity;
  const parts: [string, string][] = [['country', 'Country (2 letters)']];
  if (g !== 'country') parts.push(['state', 'State']);
  if (g === 'city' || g === 'address') parts.push(['city', 'City']);
  if (g === 'address') parts.push(['address', 'Address']);
  const set = (k: string, t: string) => {
    const next = { ...v, [k]: k === 'country' ? t.toUpperCase() : t };
    if (!next[k]) delete next[k];
    onChange(next.country ? next : undefined);
  };
  return (
    <div className="stack">
      {parts.map(([k, label]) => (
        <label key={k} className="field">
          <span className="field-label">{label}</span>
          <input id={k === 'country' ? id : undefined} className={`input${k === 'country' ? ' narrow' : ''}`} maxLength={k === 'country' ? 2 : 200} disabled={disabled} value={v[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
          {errors[k] && <span className="error">{errors[k]}</span>}
        </label>
      ))}
      {errors[''] && <div className="error">{errors['']}</div>}
    </div>
  ); // prettier-ignore
}

function MatrixField({
  q,
  value,
  onChange,
  errors,
  disabled,
}: {
  q: Extract<QuestionDefinition, { type: 'matrix' }>;
  value: unknown;
  onChange: (v: unknown) => void;
  errors: Record<string, string>;
  disabled?: boolean;
}) {
  // prettier-ignore
  const v = (value as Record<string, string | string[]>) ?? {};
  const { rows, columns, multiple } = q.settings;
  const set = (row: string, col: string) => {
    const next = { ...v };
    if (multiple) {
      const cur = new Set((next[row] as string[]) ?? []);
      if (cur.has(col)) cur.delete(col);
      else cur.add(col);
      const arr = columns.map((c) => c.value).filter((c) => cur.has(c));
      if (arr.length) next[row] = arr;
      else delete next[row];
    } else next[row] = col;
    onChange(Object.keys(next).length ? next : undefined);
  };
  const on = (row: string, col: string) =>
    multiple ? ((v[row] as string[]) ?? []).includes(col) : v[row] === col;
  return (
    <div className="table-wrap">
      <table className="grid matrix">
        <thead>
          <tr>
            <th />
            {columns.map((c) => (
              <th key={c.value}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>
                {r.label}
                {errors[r.key] && <div className="error">{errors[r.key]}</div>}
              </td>
              {columns.map((c) => (
                <td key={c.value} className="center">
                  <input
                    type={multiple ? 'checkbox' : 'radio'}
                    name={`m-${r.key}`}
                    aria-label={`${r.label}: ${c.label}`}
                    checked={on(r.key, c.value)}
                    disabled={disabled}
                    onChange={() => set(r.key, c.value)}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {errors[''] && <div className="error">{errors['']}</div>}
    </div>
  );
}

/** Rows of typed cells, for tables and repeating groups. Empty optional cells are left out. */
function RowsField({
  columns,
  label,
  min,
  max,
  value,
  onChange,
  errors,
  disabled,
  stacked,
}: {
  columns: Column[];
  label: string;
  min: number;
  max: number;
  value: unknown;
  onChange: (v: unknown) => void;
  errors: Record<string, string>;
  disabled?: boolean;
  stacked?: boolean;
}) {
  // prettier-ignore
  const [rows, setRows] = useState<Record<string, string>[]>(() =>
    Array.isArray(value)
      ? (value as Record<string, unknown>[]).map((r) =>
          Object.fromEntries(Object.entries(r).map(([k, x]) => [k, x === null || x === undefined ? '' : String(x)])),
        )
      : Array.from({ length: Math.max(min, 1) }, () => ({})),
  );
  const emit = (next: Record<string, string>[]) => {
    setRows(next);
    const typed = next.map((r) =>
      Object.fromEntries(
        columns.flatMap((c) => {
          const raw = r[c.key] ?? '';
          if (raw === '') return [];
          const v = c.type === 'number' ? Number(raw) : c.type === 'boolean' ? raw === 'true' : raw;
          return [[c.key, v]];
        }),
      ),
    );
    onChange(typed.length ? typed : undefined);
  };
  const cell = (i: number, c: Column) => {
    const v = rows[i]![c.key] ?? '';
    const set = (x: string) => emit(rows.map((r, j) => (j === i ? { ...r, [c.key]: x } : r)));
    const name = `${label} ${i + 1}: ${c.label}`;
    if (c.type === 'boolean' || c.type === 'choice')
      return (
        <select
          className="input"
          aria-label={name}
          disabled={disabled}
          value={v}
          onChange={(e) => set(e.target.value)}
        >
          <option value="">{c.required ? 'Choose…' : '—'}</option>
          {(c.type === 'boolean' ? ['true', 'false'] : (c.options ?? [])).map((o) => (
            <option key={o} value={o}>
              {c.type === 'boolean' ? (o === 'true' ? 'Yes' : 'No') : o}
            </option>
          ))}
        </select>
      );
    return (
      <input
        className="input"
        aria-label={name}
        type={c.type === 'date' ? 'date' : 'text'}
        inputMode={c.type === 'number' ? 'decimal' : undefined}
        disabled={disabled}
        value={v}
        onChange={(e) => set(e.target.value)}
      />
    );
  };
  const remove = (i: number) => emit(rows.filter((_, j) => j !== i));
  return (
    <div className="stack">
      {stacked ? (
        rows.map((_, i) => (
          <fieldset key={i} className="group-item">
            <legend>
              {label} {i + 1}
            </legend>
            {columns.map((c) => (
              <label key={c.key} className="field">
                <span className="field-label">
                  {c.label}
                  {c.required ? '' : ' (optional)'}
                </span>
                {cell(i, c)}
                {errors[`${i}.${c.key}`] && <span className="error">{errors[`${i}.${c.key}`]}</span>}
              </label>
            ))}
            <button type="button" className="btn btn-sm btn-ghost" disabled={disabled || rows.length <= min} onClick={() => remove(i)}>
              <Trash2 size={14} /> Remove
            </button>
          </fieldset>
        ))
      ) : (
        <div className="table-wrap">
          <table className="grid rows">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((_, i) => (
                <tr key={i}>
                  {columns.map((c) => (
                    <td key={c.key}>
                      {cell(i, c)}
                      {errors[`${i}.${c.key}`] && <div className="error">{errors[`${i}.${c.key}`]}</div>}
                    </td>
                  ))}
                  <td>
                    <button type="button" className="btn btn-icon btn-ghost" aria-label={`Remove ${label.toLowerCase()} ${i + 1}`} disabled={disabled || rows.length <= min} onClick={() => remove(i)}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div>
        <button type="button" className="btn btn-sm" disabled={disabled || rows.length >= max} onClick={() => emit([...rows, {}])}>
          <Plus size={14} /> Add {label.toLowerCase()}
        </button>
      </div>
      {errors[''] && <div className="error">{errors['']}</div>}
    </div>
  ); // prettier-ignore
}
